import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import siteConfig from '@/config/siteConfig';

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <motion.div
    initial={false}
    className="border-b border-border/30 last:border-b-0"
  >
    <button
      onClick={onClick}
      className="w-full py-6 flex items-start justify-between gap-4 text-left group"
    >
      <span className="font-display text-base md:text-lg text-foreground group-hover:text-accent transition-colors">
        {question}
      </span>
      <motion.span
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.3 }}
        className="shrink-0 mt-1"
      >
        <ChevronDown className="w-5 h-5 text-accent" />
      </motion.span>
    </button>
    <motion.div
      initial={false}
      animate={{
        height: isOpen ? 'auto' : 0,
        opacity: isOpen ? 1 : 0
      }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <div className="pb-6 text-muted-foreground font-body leading-relaxed text-sm md:text-base">
        {typeof answer === 'string' ? answer : answer}
      </div>
    </motion.div>
  </motion.div>
);

export const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "\"J'ai déjà fait plein de trucs.\"",
      answer: "Parfait. Ici, tu ne viens pas ajouter une couche. Tu viens changer le niveau d'accès : état → intégration → stabilisation."
    },
    {
      question: "\"Je n'ai pas le temps.\"",
      answer: "Tu n'as pas besoin de 2 heures par jour. Tu as besoin d'un réglage régulier qui te remet dans l'axe. Et avec l'accès à vie, tu peux avancer à ton rythme et y revenir au besoin."
    },
    {
      question: "\"Et si je retombe ?\"",
      answer: "C'est précisément pour ça qu'il y a intégration + stabilisation. Pas juste un déclic. Un nouveau normal."
    },
    {
      question: "\"Et si je ne me sens pas prêt(e) ?\"",
      answer: "La vraie question : combien de temps tu veux encore vivre en dessous de ce qui t'appelle ?"
    },
    {
      question: "\"J'ai peur d'être trop sensible.\"",
      answer: "Ici, on ne t'excite pas : on te stabilise. On ne te pousse pas : on te calibre."
    },
    {
      question: "Je n'ai pas beaucoup de ressentis, est-ce que cela va fonctionner sur moi ?",
      answer: "Bien sûr ! Mais je ne vais pas te mentir, ces énergies sont subtiles. Alors au début, tu auras peut-être l'impression que rien ne se passe. Mais tu vas constater que ton humeur change, ta façon de penser et d'agir se modifient. Très vite, tu vas ressentir l'énergie des dauphins qui t'accompagne au quotidien."
    },
    {
      question: "Je débute en spiritualité, est-ce que je peux m'inscrire ?",
      answer: "Bien sûr ! Cette formation est conçue pour t'accompagner où que tu en sois dans ton cheminement spirituel. Le chemin est progressif, tu peux avancer à ton rythme et prendre tout le temps nécessaire pour intégrer ces puissantes énergies."
    },
    {
      question: "Je suis thérapeute, en quoi les maîtres dauphins peuvent-ils m'être utiles dans ma pratique ?",
      answer: (
        <span>
          De nombreux·se thérapeutes choisissent de rejoindre l'École de Maîtres Dauphins pour aller plus loin dans leur pratique. 
          Ils choisissent de s'initier à la sagesse des Maîtres Dauphins pour enrichir leur pratique et apporter des solutions uniques et innovantes à leurs client·es.{' '}
          <a href={siteConfig.therapistLink} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            Plus d'infos sur la page thérapeutes
          </a>
        </span>
      )
    },
    {
      question: "Des fréquences dauphins, c'est pas un peu \"perché\" cette histoire ?",
      answer: "Je ne te le fais pas dire ! J'ai été le premier à être surpris lorsque j'ai reçu ces transmissions delphiques par canalisation. J'étais plutôt cartésien avec mon parcours de juriste spécialisé en criminologie et droit pénal. Mais quand j'ai fait confiance et que j'ai vu à quel point les Maîtres dauphins ont transformé ma vie… J'ai accepté de ne pas tout savoir et de ne pas tout comprendre !"
    },
    {
      question: "J'ai déjà essayé beaucoup de choses. En quoi l'école des maîtres dauphins est différente ?",
      answer: "Avec les fréquences des maîtres dauphins, tu vas entrer dans un tout autre niveau de conscience : celui du monde quantique. Ces fréquences envoient de puissants codes dans tes cellules et l'eau dont ton corps est composé à 60%. Le vrai plus de cette méthode, c'est qu'à chaque module tu effectues un soin d'intégration qui permet à cette nouvelle énergie de s'ancrer en toi durablement."
    }
  ];

  return (
    <section id="faq" ref={ref} className="section-padding bg-background relative overflow-hidden">
      {/* Cosmic background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-stellar" />
      </div>

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-gold-luminous font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Questions fréquentes
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight">
            Tu as encore des questions ? 
            <span className="text-gradient-gold"> J'y réponds !</span>
          </h2>
        </motion.div>

        {/* FAQ List */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <div className="luxury-card rounded-3xl p-6 md:p-10">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
          </div>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-muted-foreground font-body">
            Tu as d'autres questions ? Envoie-moi un email à : {' '}
            <a href={`mailto:${siteConfig.email}`} className="text-accent hover:underline">
              {siteConfig.email}
            </a>
            {' '}et je te répondrai avec joie.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
